import {
  Box,
  Button,
  Center,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  type PopoverBodyProps,
  type PopoverContentProps,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Text,
} from "@chakra-ui/react";
import {
  formatDate,
  InputRangeDatePicker,
  type IOption,
  MultiMonthCalendar,
} from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  getDateRangePresets,
  getDateRangePresetTitle,
} from "@src/components/ui-kit/datePicker/CalendarActions";
import { Icon } from "@src/components/ui-kit/Icon";
import type { LegacySelectProps } from "@src/components/ui-kit/Select/LegacySelect";
import { VirtualListDynamic } from "@src/components/ui-kit/VirtualList/dynamic";
import { toApiDate } from "@src/utils/dates";
import type { CalendarValues } from "@uselessdev/datepicker";
import { groupBy, map } from "lodash";
import { observer } from "mobx-react-lite";
import React, {
  type FC,
  type FunctionComponent,
  type ReactNode,
  useRef,
} from "react";
import { FilterOptionButton } from "./components/filter-option-button";
import { PaginatedFilter } from "./components/paginated-filter";
import type { Filter } from "./models";

export interface TableFilter {
  key: string;
  label: string;
  filter: ReactNode;
  belowContent?: ReactNode;
  selectedCount: number;
  selectedLabel: string;
  selectedTooltip?: string;
  isCollapsed?: boolean;
  popoverBodyProps?: PopoverBodyProps;
  popoverContentProps?: PopoverContentProps;
}

function formatSelectedOptionsLabel(
  selected: { label: string }[],
  emptyLabel: string,
  totalCount = selected.length,
): string {
  if (selected.length === 0) return emptyLabel;
  const extra = totalCount - 1;
  return extra > 0 ? `${selected[0].label} +${extra}` : selected[0].label;
}

function formatSelectedOptionsTooltip(
  selected: { label: string }[],
): string | undefined {
  if (selected.length < 2) return undefined;
  return selected.map((option) => option.label).join(", ");
}

function formatFilterSelectedTooltip(
  filter: Filter<unknown>,
): string | undefined {
  if (filter.dynamicPreset) return formatDateFilterLabel(filter.value);
  if (filter.dateRange || filter.date) return undefined;
  return formatSelectedOptionsTooltip(filter.selectedOptions);
}

function formatDateFilterLabel(values: string[]): string {
  const dates = values.filter(Boolean);
  if (dates.length === 0) return t`Any`;
  return dates.map((value) => formatDate(new Date(value))).join(" – ");
}

function formatFilterSelectedLabel(filter: Filter<unknown>): string {
  if (filter.dynamicPreset) {
    return getDateRangePresetTitle(filter.dynamicPreset) ?? t`Any`;
  }

  if (filter.dateRange || filter.date) {
    return formatDateFilterLabel(filter.value);
  }

  if (filter.value.length === 0) return t`Any`;

  const selected = filter.selectedOptions;
  if (selected.length === 0) {
    return filter.value[0] ?? t`Any`;
  }

  return formatSelectedOptionsLabel(selected, t`Any`, filter.value.length);
}

export const createChoiceListFilter = (
  filters: Filter<any>[],
): TableFilter[] => {
  return map(filters, (f) => {
    return {
      key: f.column,
      label: f.title,
      isCollapsed: f.isCollapsed,
      get selectedCount() {
        return f.value.length;
      },
      get selectedLabel() {
        return formatFilterSelectedLabel(f);
      },
      get selectedTooltip() {
        return formatFilterSelectedTooltip(f);
      },
      filter: (
        <CustomChoiceList
          filter={f}
          autoFocus={100}
          hasSelectAllOption={f.hasSelectAllOption}
        />
      ),
    };
  });
};

export const createPaginatedFilters = (
  filters: Filter<any>[],
): TableFilter[] => {
  return filters.map((filter) => ({
    key: filter.column,
    label: filter.title,
    isCollapsed: filter.isCollapsed,
    get selectedCount() {
      return filter.value.length;
    },
    get selectedLabel() {
      return formatFilterSelectedLabel(filter);
    },
    get selectedTooltip() {
      return formatFilterSelectedTooltip(filter);
    },
    filter: (
      <Box maxW="30rem">
        <PaginatedFilter filter={filter} />
      </Box>
    ),
  }));
};

const CustomChoiceList: FunctionComponent<{
  filter: Filter<any>;
  autoFocus: LegacySelectProps["autoFocus"];
  hasSelectAllOption?: boolean;
}> = observer(function CustomChoiceList({
  filter,
  autoFocus,
  hasSelectAllOption,
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Box maxW="30rem">
      <KeepPopoverOpen>
        <HStack
          pos="relative"
          overflowY="auto"
          h="fit-content"
          minH="10"
          maxH="24"
          mb="2"
          px="1"
          border="1px"
          borderColor="gray.200"
          _focusWithin={{
            borderColor: "purple.500",
            boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
          }}
          transition="all"
          onClick={() => {
            inputRef.current?.focus();
          }}
          rounded="md"
        >
          <HStack wrap="wrap" px="2" py="2" cursor="text">
            {filter.selectedOptions.map((option) => (
              <Tag
                key={`${option.value}-${option.label}`}
                pr="0"
                wordBreak="keep-all"
                size="sm"
              >
                {option.label}
                <IconButton
                  ml="1"
                  aria-label={t`Filter option deselect`}
                  colorScheme="red"
                  data-group
                  icon={
                    <Icon
                      name="x-close"
                      color="gray.700"
                      _groupHover={{
                        color: "red.500",
                      }}
                    />
                  }
                  onClick={() => {
                    filter.removeOptionFromValue(option);
                  }}
                  size="xs"
                  variant="ghost"
                />
              </Tag>
            ))}
            <Input
              ref={inputRef}
              flex="1"
              display="flex"
              minW="24"
              h="fit-content"
              autoFocus={Boolean(autoFocus)}
              onChange={({ target }) => {
                filter.setInputValue(target.value);
              }}
              onKeyDown={filter.handleKeyDown}
              placeholder={filter.title}
              value={filter.inputValue}
              variant="unstyled"
            />
          </HStack>
          {filter.isMultiSelect && filter.value.length && (
            <Box pos="sticky" top="2" right="0" bottom="0" h="full">
              <Center h="full">
                <IconButton
                  ml="1"
                  aria-label={t`Clears all selected options`}
                  colorScheme="red"
                  data-group
                  icon={
                    <Icon
                      name="x-close"
                      color="gray.700"
                      _groupHover={{
                        color: "red.500",
                      }}
                    />
                  }
                  onClick={() => {
                    filter.clear();
                  }}
                  size="xs"
                  variant="ghost"
                />
              </Center>
            </Box>
          )}
        </HStack>
        {hasSelectAllOption && (
          <Button
            w="full"
            mb="2"
            px="3"
            py="2"
            _hover={{
              bg: "violet.50",
            }}
            transition="all"
            onClick={filter.handleSelectAll}
            variant="link"
          >
            <Text w="full" textAlign="start">
              {filter.hasSelectedAllOptions ? (
                <Trans>Unselect all</Trans>
              ) : (
                <Trans>Select all</Trans>
              )}
            </Text>
          </Button>
        )}
        {filter.options.length ? (
          <VirtualListDynamic
            items={filter.notSelectedOptions}
            itemRenderer={(item, key) => (
              <Box key={key}>
                {filter.hasInnerOptions ? (
                  <Box>
                    {item.options?.length && item.options.length > 0 ? (
                      <Box mb="2">
                        <Text
                          pl="3"
                          color="grey.400"
                          fontSize="xs"
                          textTransform="uppercase"
                          userSelect="none"
                          cursor="default"
                        >
                          {item.label}
                        </Text>
                        {item.options.map((innerItem) => (
                          <CustomChoiceListItem
                            key={innerItem.value}
                            item={innerItem}
                            onClick={() => {
                              filter.handleOptionClick(innerItem);
                            }}
                          />
                        ))}
                      </Box>
                    ) : null}
                  </Box>
                ) : (
                  <CustomChoiceListItem
                    item={item}
                    onClick={() => {
                      filter.handleOptionClick(item);
                    }}
                  />
                )}
              </Box>
            )}
            estimatedSize={35}
            wrapperStyle={{
              minW: "74",
              h: "300px",
              display: "flex",
              flex: "1",
            }}
          />
        ) : (
          <Center h="150px">
            <Text color="grey.300">
              {filter.noOptionsMessage ?? t`No options`}
            </Text>
          </Center>
        )}
        {filter.bottomExtraContent}
      </KeepPopoverOpen>
    </Box>
  );
});

type CustomChoiceListItemProps = {
  item: IOption;
  onClick: () => void;
};

const CustomChoiceListItem = observer(function CustomChoiceListItem({
  item,
  onClick,
}: CustomChoiceListItemProps) {
  return (
    <FilterOptionButton
      alignItems="start"
      w="full"
      h="35px"
      maxH="35px"
      px="3"
      fontSize="sm"
      fontWeight="normal"
      textAlign="left"
      label={item.label}
      _hover={{
        bg: "violet.50",
      }}
      onClick={onClick}
      variant="unstyled"
    />
  );
});

export const createDateRangeFilters = (
  filters: Filter<any>[],
): TableFilter[] => {
  return map(filters, (f) => {
    return {
      key: f.column,
      label: f.title,
      isCollapsed: f.isCollapsed,
      get selectedCount() {
        return f.value.length;
      },
      get selectedLabel() {
        return formatFilterSelectedLabel(f);
      },
      get selectedTooltip() {
        return formatFilterSelectedTooltip(f);
      },
      get belowContent() {
        return f.dateRange ? <DynamicRangeCheckbox filter={f} /> : undefined;
      },
      filter: <DateRangeFilter filter={f} />,
      get popoverContentProps() {
        return f.isDynamic ? {} : { border: "none", w: "auto" };
      },
      get popoverBodyProps() {
        return f.isDynamic ? {} : { p: 0 };
      },
    };
  });
};

const DynamicRangeCheckbox: FunctionComponent<{ filter: Filter<any> }> =
  observer(function DynamicRangeCheckbox({ filter }) {
    return (
      <Checkbox
        isChecked={filter.isDynamic}
        onChange={filter.toggleDynamic}
        pb="3"
        size="sm"
      >
        <Text color="grey.500" fontSize="sm">
          <Trans>Dynamic range</Trans>
        </Text>
      </Checkbox>
    );
  });

const DateRangeFilter: FunctionComponent<
  React.PropsWithChildren<{
    filter: Filter<any>;
  }>
> = observer(function DateRangeFilter({ filter }) {
  if (filter.isDynamic) return <DynamicRangeOptions filter={filter} />;

  return <DateRangeCalendar filter={filter} />;
});

const DynamicRangeOptions: FunctionComponent<{ filter: Filter<any> }> =
  observer(function DynamicRangeOptions({ filter }) {
    return (
      <KeepPopoverOpen>
        {getDateRangePresets().map((preset) => (
          <FilterOptionButton
            key={preset.key}
            w="full"
            h="35px"
            px="3"
            fontSize="sm"
            fontWeight={filter.dynamicPreset === preset.key ? "bold" : "normal"}
            label={preset.title}
            _hover={{
              bg: "violet.50",
            }}
            onClick={() => {
              filter.setDynamicPreset(preset.key);
            }}
            variant="unstyled"
          />
        ))}
      </KeepPopoverOpen>
    );
  });

const DateRangeCalendar: FunctionComponent<{ filter: Filter<any> }> = observer(
  function DateRangeCalendar({ filter }) {
    const [dates, setDates] = React.useState<CalendarValues>({
      start: filter.value[0] ? new Date(filter.value[0]) : undefined,
      end: filter.value[1] ? new Date(filter.value[1]) : undefined,
    });

    return (
      <KeepPopoverOpen>
        <MultiMonthCalendar
          value={dates}
          onSelectDate={(dates) => {
            if (typeof dates === "number") return;
            if (!("start" in dates)) return;
            setDates(dates);
            if (!dates?.start || !dates?.end) return;
            filter.setValue([toApiDate(dates.start), toApiDate(dates.end)]);
          }}
        />
      </KeepPopoverOpen>
    );
  },
);

export enum MultiColumnFilterTypeEnum {
  List = "list",
  Tabs = "tabs",
  DatePicker = "date-picker",
}
const getMultiColumnElement = (
  filters: Filter<any>[],
  type: MultiColumnFilterTypeEnum,
) => {
  switch (type) {
    case MultiColumnFilterTypeEnum.DatePicker:
      return <CustomDatePicker filters={filters} />;
    case MultiColumnFilterTypeEnum.List:
      return map(filters, (f) => (
        <CustomChoiceList filter={f} autoFocus={true} />
      ));
    case MultiColumnFilterTypeEnum.Tabs:
      return <CustomTabList filters={filters} />;
  }
};

export const createMultiColumnFilter = (
  filters: Filter<any>[],
  type: MultiColumnFilterTypeEnum,
): TableFilter[] => {
  const groupedFilters = groupBy(filters, (f) => f.multiColumn?.label);

  return (
    map(Object.entries(groupedFilters), ([key, value]) => ({
      key: value[0].column,
      label: key,
      isCollapsed: value[0].isCollapsed,
      get selectedCount() {
        return value.reduce((count, f) => count + f.value.length, 0);
      },
      get selectedLabel() {
        if (type === MultiColumnFilterTypeEnum.DatePicker) {
          return formatDateFilterLabel(value.flatMap((f) => f.value));
        }

        return formatSelectedOptionsLabel(
          value.flatMap((f) => f.selectedOptions),
          t`Any`,
        );
      },
      get selectedTooltip() {
        if (type === MultiColumnFilterTypeEnum.DatePicker) return undefined;
        return formatSelectedOptionsTooltip(
          value.flatMap((f) => f.selectedOptions),
        );
      },
      filter: getMultiColumnElement(filters, type),
    })) || []
  );
};

const CustomTabList: FunctionComponent<{ filters: Filter<any>[] }> = observer(
  function CustomTabList({ filters }) {
    return (
      <Tabs isFitted>
        <TabList>
          {filters.map((tab) => (
            <Tab key={tab.column}>{tab.title}</Tab>
          ))}
        </TabList>
        <TabPanels>
          {filters.map((tab, index) => (
            <TabPanel key={tab.column}>
              <CustomChoiceList filter={filters[index]} autoFocus={true} />
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    );
  },
);

const CustomDatePicker: FunctionComponent<{ filters: Filter<any>[] }> =
  observer(function CustomDatePicker({ filters }) {
    return (
      <InputRangeDatePicker
        selected={
          filters[0].value[0] && filters[1].value[0]
            ? {
                start: new Date(filters[0].value[0]),
                end: new Date(filters[1].value[0]),
              }
            : undefined
        }
        onSelect={(range) => {
          if (range) {
            filters[0].setValue(toApiDate(range.start));
            filters[1].setValue(toApiDate(range.end));
          }
        }}
      />
    );
  });

const KeepPopoverOpen: FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <button
      type="button"
      style={{ all: "unset", width: "100%", display: "contents" }}
      onClick={(event) => event.stopPropagation()}
    >
      {children}
    </button>
  );
};

export const createAmountFilter = (filters: Filter<any>[]): TableFilter[] => {
  return map(filters, (f) => {
    return {
      key: f.column,
      label: f.title,
      isCollapsed: f.isCollapsed,
      get selectedCount() {
        return f.value.length;
      },
      get selectedLabel() {
        return formatFilterSelectedLabel(f);
      },
      get selectedTooltip() {
        return formatFilterSelectedTooltip(f);
      },
      filter: <AmountFilter filter={f} />,
    };
  });
};

const AmountFilter: FunctionComponent<{
  filter: Filter<any>;
}> = observer(function AmountFilter({ filter }) {
  return (
    <Box maxW="30rem" p="1">
      <FormControl>
        <FormLabel>{filter.title}</FormLabel>
        <Input
          onChange={({ target }) => {
            if (target.value === "") {
              filter.clear();
              return;
            }
            filter.setValue(target.value);
          }}
          placeholder={t`Enter amount`}
          type="number"
          value={filter.value[0] || ""}
        />
      </FormControl>
    </Box>
  );
});
